/**
 * ruteoOSRM.js
 * 
 * Servicio para calcular rutas optimizadas usando OSRM (Open Source Routing Machine)
 * 
 * Qué hace:
 * - Calcula la mejor ruta entre múltiples puntos usando el algoritmo de TSP (Traveling Salesman Problem)
 * - Obtiene la geometría de la ruta (puntos intermedios) para dibujarla en el mapa
 * - Calcula la distancia total y tiempo estimado
 * 
 * Qué recibe:
 * - puntos (Array): Array de objetos con {lat, lng} representando los puntos a visitar
 * - puntoInicial (Object): {lat, lng} del punto de partida
 * 
 * Qué devuelve:
 * - Promise que resuelve a un objeto con:
 *   - ruta: Array de coordenadas [lng, lat] para dibujar la ruta
 *   - orden: Array de índices indicando el orden optimizado de visita
 *   - distancia: Distancia total en metros
 *   - duracion: Duración estimada en segundos
 * 
 * Dónde modificarlo:
 * - Para cambiar el servidor OSRM (actualmente usa uno público)
 * - Para cambiar el perfil de ruteo (driving, walking, cycling)
 * - Para agregar más información en la respuesta
 */

/**
 * Calcula la ruta optimizada entre múltiples puntos
 * 
 * @param {Array<{lat: number, lng: number}>} puntos - Puntos a visitar
 * @param {{lat: number, lng: number}} puntoInicial - Punto de partida
 * @returns {Promise<{ruta: Array, orden: Array, distancia: number, duracion: number}>}
 */
export async function calcularRutaOptimizada(puntos, puntoInicial) {
  try {
    // Validar que haya al menos un punto
    if (!puntos || puntos.length === 0) {
      throw new Error('Debe haber al menos un punto de entrega')
    }

    // Validar punto inicial
    if (!puntoInicial || !puntoInicial.lat || !puntoInicial.lng) {
      throw new Error('El punto inicial es requerido')
    }

    // Combinar punto inicial con los puntos de entrega
    const todosLosPuntos = [puntoInicial, ...puntos]

    // Convertir puntos a formato OSRM: "lng,lat;lng,lat;..."
    // OSRM usa el formato "longitud,latitud" (al revés de lo común)
    const coordenadasOSRM = todosLosPuntos
      .map(punto => `${punto.lng},${punto.lat}`)
      .join(';')

    // URL del servidor OSRM público
    // Nota: Este es un servidor público, en producción deberías usar tu propio servidor
    // Perfil "driving" es para automóviles, también hay "walking" y "cycling"
    const baseUrl = 'https://router.project-osrm.org'
    const perfil = 'driving' // Puede ser: driving, walking, cycling

    // Paso 1: Calcular la matriz de distancias entre todos los puntos
    // Esto es necesario para optimizar el orden de visita
    const matrizUrl = `${baseUrl}/table/v1/${perfil}/${coordenadasOSRM}?annotations=distance,duration`
    
    const matrizResponse = await fetch(matrizUrl)
    if (!matrizResponse.ok) {
      throw new Error(`Error al calcular matriz de distancias: ${matrizResponse.status}`)
    }
    const matrizData = await matrizResponse.json()

    // Paso 2: Optimizar el orden de visita usando un algoritmo simple
    // (En producción podrías usar un algoritmo más sofisticado como TSP)
    const ordenOptimizado = optimizarOrden(matrizData.distances, matrizData.durations)

    // Paso 3: Calcular la ruta completa siguiendo el orden optimizado
    const puntosOrdenados = ordenOptimizado.map(indice => todosLosPuntos[indice])
    const coordenadasOrdenadas = puntosOrdenados
      .map(punto => `${punto.lng},${punto.lat}`)
      .join(';')

    // Obtener la geometría de la ruta (puntos intermedios para dibujar)
    const rutaUrl = `${baseUrl}/route/v1/${perfil}/${coordenadasOrdenadas}?overview=full&geometries=geojson`
    
    const rutaResponse = await fetch(rutaUrl)
    if (!rutaResponse.ok) {
      throw new Error(`Error al calcular ruta: ${rutaResponse.status}`)
    }
    const rutaData = await rutaResponse.json()

    // Verificar que se encontró una ruta
    if (rutaData.code !== 'Ok' || !rutaData.routes || rutaData.routes.length === 0) {
      throw new Error('No se pudo calcular una ruta válida')
    }

    const ruta = rutaData.routes[0]

    // Extraer las coordenadas de la ruta (formato GeoJSON)
    // GeoJSON usa [lng, lat], pero Leaflet usa [lat, lng], así que las invertimos
    const coordenadasRuta = ruta.geometry.coordinates.map(coord => [coord[1], coord[0]])

    // Retornar la información completa
    return {
      ruta: coordenadasRuta, // Array de [lat, lng] para Leaflet
      orden: ordenOptimizado, // Orden optimizado de visita (índices)
      distancia: ruta.distance, // Distancia total en metros
      duracion: ruta.duration, // Duración estimada en segundos
      puntosOrdenados: puntosOrdenados // Puntos en el orden optimizado
    }
  } catch (error) {
    throw new Error(`Error al calcular ruta optimizada: ${error.message}`)
  }
}

/**
 * Optimiza el orden de visita de los puntos usando un algoritmo greedy simple
 * 
 * @param {Array<Array<number>>} distancias - Matriz de distancias entre puntos
 * @param {Array<Array<number>>} duraciones - Matriz de duraciones entre puntos
 * @returns {Array<number>} Array de índices representando el orden optimizado
 */
function optimizarOrden(distancias, duraciones) {
  // Algoritmo greedy: siempre ir al punto más cercano no visitado
  const numPuntos = distancias.length
  const visitados = new Set([0]) // Empezamos en el punto 0 (punto inicial)
  const orden = [0] // El orden empieza con el punto inicial

  // Mientras no hayamos visitado todos los puntos
  while (visitados.size < numPuntos) {
    let puntoActual = orden[orden.length - 1]
    let distanciaMinima = Infinity
    let siguientePunto = -1

    // Buscar el punto no visitado más cercano
    for (let i = 0; i < numPuntos; i++) {
      if (!visitados.has(i)) {
        const distancia = distancias[puntoActual][i]
        if (distancia < distanciaMinima) {
          distanciaMinima = distancia
          siguientePunto = i
        }
      }
    }

    // Si encontramos un punto, agregarlo al orden
    if (siguientePunto !== -1) {
      orden.push(siguientePunto)
      visitados.add(siguientePunto)
    } else {
      break
    }
  }

  return orden
}

